<template>
  <section class="section">
    <h1>{{ user.email }}</h1>
    <form
      @submit.prevent="submit"
      class="form"
    >
      <FormInput
        :type="'text'"
        :name="'name'"
        :label="$t('settings.inputs.name')"
        :value="formFields.name"
        :error="getError('name')"
        :input="event => handleChange(event, 'name')"
        :blur="() => handleBlur('name')"
        :disabled="submitInProgress"
      />
      <FormInput
        :type="'text'"
        :name="'phone_number'"
        :label="$t('settings.inputs.phoneNumber')"
        :value="formFields.phone_number"
        :error="getError('phone_number')"
        :input="event => handleChange(event, 'phone_number')"
        :blur="() => handleBlur('phone_number')"
        :disabled="submitInProgress"
      />
      <div v-if="passwordReset">
        <FormInput
          :type="'password'"
          :name="'password'"
          :label="$t('settings.inputs.password')"
          :value="formFields.password"
          :error="getError('password')"
          :input="event => handleChange(event, 'password')"
          :blur="() => handleBlur('password')"
          :disabled="submitInProgress"
        />
        <FormInput
          :type="'password'"
          :name="'password_new'"
          :label="$t('settings.inputs.passwordNew')"
          :value="formFields.password_new"
          :error="getError('password_new')"
          :input="event => {
            handleChange(event, 'password_new');

            if (getError('password_new_repeat').length > 0) {
              validateField('password_new_repeat');
            }
          }"
          :blur="() => {
            handleBlur('password_new');

            if (getError('password_new_repeat').length > 0) {
              validateField('password_new_repeat');
            }
          }"
          :disabled="submitInProgress"
        />
        <FormInput
          :type="'password'"
          :name="'password_new_repeat'"
          :label="$t('settings.inputs.passwordNewRepeat')"
          :value="formFields.password_new_repeat"
          :error="getError('password_new_repeat')"
          :input="event => {
            handleChange(event, 'password_new_repeat');

            if (getError('password_new').length > 0) {
              validateField('password_new');
            }
          }"
          :blur="() => {
            handleBlur('password_new_repeat');

            if (getError('password_new').length > 0) {
              validateField('password_new');
            }
          }"
          :disabled="submitInProgress"
        />
      </div>
      <p class="lastPasswordReset">
        {{ $t('settings.lastPasswordResetAt') }}: {{ getPasswordResetDate() }}
      </p>
      <a
        v-if="!passwordReset"
        @click="passwordReset = true"
        class="passwordReset"
      >
        {{ $t('settings.passwordReset') }}
      </a>
      <FormButton
        v-if="formDirty"
        :type="'submit'"
        :action="() => {}"
        :label="$t('settings.buttonSubmit')"
        :disabled="submitInProgress"
      />
    </form>
  </section>
</template>

<style scoped>
  .section {
    margin: 100px 0;
  }

  .form {
    position: relative;
    display: flex;
    width: 400px;
    max-width: 400px;
    flex-direction: column;
    padding: 50px 0 0 0;
  }

  .lastPasswordReset {
    margin: 0 0 20px 0;
  }

  .passwordReset {
    color: var(--primary-color);
    cursor: pointer;
    transition: color 0.2s;
  }

  .passwordReset:hover {
    color: var(--secondary-color);
  }
</style>

<script>
  import { validateSingleField } from '../pages/userValidation.js';
  import FormInput from '../form/FormInput.vue';
  import FormButton from '../form/FormButton.vue';

  export default {
    components: {
      FormInput,
      FormButton
    },
    props: [ 'user' ],
    data() {
      return {
        formFields: {
          name: '',
          phone_number: '',
          password: '',
          password_new: '',
          password_new_repeat: ''
        },
        formErrors: {},
        formDirty: false,
        passwordReset: false,
        passwordResetAt: null,
        submitInProgress: false
      }
    },
    created: async function() {
      this.formFields.name = this.user.name;
      this.formFields.phone_number = this.user.phone_number ?? '';
    },
    methods: {
      getError(key) {
        if (this.formErrors.hasOwnProperty(key)) {
          return this.formErrors[key];
        }
        
        return '';
      },
      handleChange(event, key) {
        if (!this.formDirty) {
          this.formDirty = true;
        }

        this.formFields[key] = event.target.value;

        const currentError = this.getError(key);

        if (currentError.length > 0) {
          const message = validateSingleField(this.formFields, key);

          if (message.length > 0) {
            this.formErrors[key] = message;
          } else {
            delete this.formErrors[key];
          }
        }
      },
      handleBlur(key) {
        this.validateField(key);
      },
      validateField(key) {
        const message = validateSingleField(this.formFields, key);
  
        if (message.length > 0) {
          const currentError = this.getError(key);
  
          if (currentError !== message) {
            this.formErrors[key] = message;
          }
        } else {
          delete this.formErrors[key];
        }

        this.$forceUpdate();
      },
      getPasswordResetDate() {
        const value = this.passwordResetAt || this.user.password_reset_at;
        const date = new Date(Date.parse(value));

        const dateParts = [
          date.getDate().toString().padStart(2, '0'),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getFullYear().toString()
        ];

        const timeParts = [
          date.getHours().toString().padStart(2, '0'),
          date.getMinutes().toString().padStart(2, '0'),
          date.getSeconds().toString().padStart(2, '0')
        ];

        return `${dateParts.join('-')} ${timeParts.join(':')}`;
      },
      submit: async function() {
        this.validateField('name');
        this.validateField('phone_number');

        if (this.formFields.password.length > 0
          || this.formFields.password_new.length > 0
          || this.formFields.password_new_repeat.length > 0
        ) {
          this.validateField('password');
        }

        if (this.formFields.password_new.length > 0 || this.formFields.password_new_repeat.length > 0) {
          this.validateField('password_new');
          this.validateField('password_new_repeat');
        }

        if (this.getError('name').length > 0
          || this.getError('phone_number').length > 0
          || this.getError('password').length > 0
          || this.getError('password_new').length > 0
          || this.getError('password_new_repeat').length > 0
        ) {
          return;
        }

        this.submitInProgress = true;

        const response = await fetch(`/api/user/${this.user.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.formFields)
        });

        this.formFields = {
          ...this.formFields,
          password: '',
          password_new: '',
          password_new_repeat: ''
        };

        this.formErrors = {
          ...this.formErrors,
          password: '',
          password_new: '',
          password_new_repeat: ''
        };

        const responseJson = await response.json();

        switch (response.status) {
          case 200:
            this.formDirty = false;
            this.passwordReset = false;
            this.passwordResetAt = responseJson.password_reset_at;
            break;
          case 400:
            this.formErrors = {
              ...this.formErrors,
              name: responseJson?.name?.join(' ') ?? '',
              phone_number: responseJson?.phone_number?.join(' ') ?? '',
              password: responseJson?.password?.join(' ') ?? '',
              password_new: responseJson?.password_new?.join(' ') ?? '',
              password_new_repeat: responseJson?.password_new_repeat?.join(' ') ?? ''
            }
            break;
          case 401:
            this.formErrors.password = this.$t('settings.errors.invalidCredentials');
            break;
          default:
            window.location.href = '/';
        }

        this.submitInProgress = false;
      }
    }
  }
</script>
